/**
 * Origin A's first hour on the 256 m plains (docs/design/C0_OPENING_PLAINS.md §5–§8; data in
 * data/plainsOpening.ts). In force only with `plains256()` on the plains map; anywhere else it does nothing.
 *
 * - **The chain.** `QUESTS` is the plains chain then (data/quests.ts), so the World's accept, turn-in, kill and
 *   triangle counting, the quest strip and the save all work as before. This system adds what the elder's chain never
 *   had: givers other than the elder (ฉีหมิง, the ring master, หลู่เจิ้ง: its `talk`), the objectives the World does not
 *   count (`talk`, `visit`, `meditate`, `neigong`, `duelWin`, `sect`), quests handed in the moment they are done, and
 *   the "go here" marker (`world.story.guide`) to the next person or place.
 * - **The people.** ลุงเกา at the cart, ฉีหมิง at the ring, the ring master เหล่ยกัง, ครูฝึกเฒ่าหวัง at the training hall,
 *   the fair at the hall of sect banners (three launch sects as the World's school masters, so `joinSchool` works as
 *   it does elsewhere; the คลื่นไผ่ envoy and the fair host record a choice the sect system will read: `plainsStory.sect`),
 *   and หลู่เจิ้ง. The hub rival (systems/duelMatch.ts) is moved to the ring.
 * - **M2's spar.** ฉีหมิง fights as the yard sparrer (`sparrer-yard`), never falls and never lets the player fall, and
 *   leads the three duels in turn (he swings, then guards, then charges a break), with the hints after two misses.
 * - **M7.** The masked men and their chief at the bamboo edge (`masked`, `masked-chief`), the chief's call at half
 *   health, ฉีหมิง calling his guard and his charge, the tassel.
 * - **Moves.** A character the plains opening starts (scene `plains-opening`) learns กระบี่ไผ่ห่มหมอก a move at a time
 *   (data/moveUnlocks.ts, systems/moveLocks.ts): 掃 蘆 竹 霧 颶 颯 in the chain's order. Every other art learned is
 *   whole at once.
 *
 * Deterministic: no draws from the random stream except the World's own monster spawns; time only from `step`.
 */
import { QUESTS, type ObjectiveType, type QuestDef } from '../data/quests';
import { SCHOOLS } from '../data/schools';
import { getArt, getSkill, STARTER_ART } from '../data/skills';
import { isArmed } from '../data/weaponFamilies';
import {
  AUTO_TURN_IN,
  bySex,
  CHIEF_SPOT,
  FIRST_TITLE,
  FAIR_RECRUITERS,
  LINES,
  MASK_SPOTS,
  MASKED,
  MASKED_CHIEF,
  MIST_MOVE,
  MOVES_ON_ACCEPT,
  MOVES_ON_DONE,
  PQ,
  QIMING_M7,
  ROUTES,
  QUEST_GIVER,
  QUEST_PLACE,
  STORY_CAST,
  STORY_PLACES,
  TIDE_SECT,
  WANDERER,
  type StoryRole,
} from '../data/plainsOpening';
import { CHIEF, CHIEF_DROP, SPAR_RHYTHM, FAIR_DEMO, FAIR_TRIAL, FOLK_NEAR, GLIDE_MOVE, GLIDE_TIER, QIMING_FIGHT, TASSEL_ID } from '../data/plainsFill';
import { BAG_SIZE, getItem, ITEM_LIST, rollItem } from '../data/items';
import { plains256 } from '../data/zones';
import { duelGroundOf } from './duelGround';
import { nearestFloor } from '../path';
import { learnMove, startMoveGate } from './moveLocks';
import { duelMatchOf } from './duelMatch';
import { advanceObjectives } from '../questProgress';
import { FirstHourQuests } from './plainsQuestsF';
import type { PlayerProgress } from '../progress';
import { dist, type Entity, type PlayerCommand, type World, type WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** What the save keeps (`PlayerProgress.plainsStory`): the fair's choice when it is not a school. */
export interface PlainsStorySave {
  /** `bamboo-tide` (คลื่นไผ่, no school yet), `wanderer` (เดินทางลำพัง) or `later` (not chosen at M6). */
  sect?: 'bamboo-tide' | 'wanderer' | 'later';
  /** A trial bout's loan in force: what to give back (arts, active art, moves, bar). A save mid-trial restores it on load. */
  loan?: TrialLoan;
}

export interface TrialLoan {
  arts: string[];
  activeArt: string;
  movesLearned?: string[];
  skillBar?: (string | null)[];
}

const SECT_CHOICES = new Set(['bamboo-tide', 'wanderer', 'later']);

/** A saved story state, checked on load (anything else is dropped). */
export function parsePlainsStory(v: unknown): PlainsStorySave | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as { sect?: unknown; loan?: unknown };
  const out: PlainsStorySave = typeof r.sect === 'string' && SECT_CHOICES.has(r.sect) ? { sect: r.sect as PlainsStorySave['sect'] } : {};
  const l = r.loan as Partial<TrialLoan> | undefined;
  const strs = (a: unknown): string[] | undefined => (Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : undefined);
  if (l && typeof l === 'object' && typeof l.activeArt === 'string' && strs(l.arts)?.length) {
    const bar = Array.isArray(l.skillBar) ? l.skillBar.map((x) => (typeof x === 'string' ? x : null)) : undefined;
    out.loan = { arts: strs(l.arts)!, activeArt: l.activeArt, ...(strs(l.movesLearned) ? { movesLearned: strs(l.movesLearned)! } : {}), ...(bar ? { skillBar: bar } : {}) };
  }
  return out;
}

const SYSTEMS = new WeakMap<object, PlainsStorySystem>();
/** The plains story of a World (null when it has none). */
export const plainsStoryOf = (w: object): PlainsStorySystem | null => SYSTEMS.get(w) ?? null;

/** The sparring partner M2 is fought against (data/monsters.ts). */
const SPARRER = 'sparrer-yard';
/** Where the hub rival waits on the 256 m plains: beside the ring, south-east of its floor. */
const RIVAL_AT = [111.5, 106.5] as const;
/** Seconds a bubble of a bark stays before the next part of it (1.2 s + 0.06 s a character, §3). */
const sayFor = (text: string) => 1.2 + 0.06 * text.length;

interface Spoken {
  id: number;
  text: string;
  /** Seconds left before it is said. */
  t: number;
}

/** The mark over a person's head: a quest to take (`offer`) or hand in (`ready`), or a sect recruiter (`recruit`). */
export type NpcMark = 'offer' | 'ready' | 'recruit' | null;

export class PlainsStorySystem implements WorldSystem {
  readonly name = 'plainsStory';
  /** On: the 256 m plains, outdoors. */
  private on = false;
  private readonly people = new Map<StoryRole, Entity>();
  private readonly roleOf = new Map<number, StoryRole>();
  private readonly homes = new Map<StoryRole, { x: number; y: number }>();
  private readonly queue: PlayerCommand[] = [];
  private readonly spoken: Spoken[] = [];
  /** Lines said once a session. */
  private readonly said = new Set<string>();
  /** The last dialog offered a choice of the fair (the dialog's button sends `questAccept`). */
  private choice: string | null = null;
  /** The elder's dialog offered to leave the sect choice for later (its button sends `questTurnIn`). */
  private deferOffered = false;
  /** M2's sparring partner while the three forms are under way, and the misses counted toward a hint. */
  private spar: Entity | null = null;
  private misses = 0;
  private hinted = -1;
  private stageSaid = -1;
  private sparT = 0;
  /** M7: the chief, whether he called his men, cooldowns of ฉีหมิง's calls, the tassel's thought. */
  private chief: Entity | null = null;
  private called = false;
  private callT = 0;
  private wasDead = false;
  private artsSeen = 0;
  /** A notice to show next step (after the World's own of the same moment). */
  private late: string | null = null;
  /** The townsfolk beside the quest people (their talk turn), and the fair's demo pairs. */
  private readonly folk = new Map<number, { lines: readonly string[]; turn: number }>();
  private skipWake = false;
  private folkMade = false;
  /** The chief's gates (0: his guard broken, 1: a charge interrupted), the seconds each has held him, and his timers. */
  private readonly chiefGate: [boolean, boolean] = [false, false];
  private readonly pin: [number, number] = [0, 0];
  private chiefPhase = 0;
  private chiefGuardT = 0;
  private chiefChargeT = 0;
  /** The hero's moves used in the trial bout, and the lad's timers. */
  private readonly trialMoves = new Set<string>();
  private trialSwingT = 0;
  private trialGuardT = 0;
  private trialCrushT = 0;
  private trialTold = 0;

  private demoT = 0;
  private demoTurn = 0;
  /** The tassel is owed to the bag (the bag was full, or the chief just fell). */
  private tasselDue = false;
  /** Recruiters (school ids) who offered their trial bout, and the bout under way. */
  private readonly trialOffered = new Set<string>();
  private trial: {
    school: string;
    foe: Entity;
    t: number;
    saved: { arts: string[]; activeArt: string; movesLearned: string[] | undefined; skillBar: PlayerProgress['skillBar'] };
  } | null = null;
  /** ฉีหมิง's next blow in M7. */
  private qimingT = 0;
  /** The first-hour fixes: the lock form (F1), the stones (F2), the cart (F3). */
  private readonly fixes: FirstHourQuests;

  constructor(private readonly h: SystemHost) {
    this.fixes = new FirstHourQuests({
      h,
      at: (id, st) => this.at(id, st),
      bump: (t, tg) => this.bump(t, tg),
      bark: (id, text, after) => this.bark(id, text, after),
      sex: () => this.sex(),
    });
  }

  /** The cart of F3 while it is under way (for the client), else null. */
  get escort() {
    return this.fixes.escort;
  }

  // ───────────────────────── setup ─────────────────────────

  setup(): void {
    const w = this.h.world;
    SYSTEMS.set(w, this);
    this.on = plains256() && w.map.zone === 'plains' && !w.map.interior;
    if (!this.on) return;
    // Saved in the middle of a trial bout: the loaned art is given back at once.
    const loan = w.progress.plainsStory?.loan;
    if (loan) {
      const pr = w.progress;
      pr.arts = loan.arts;
      pr.activeArt = loan.activeArt;
      pr.movesLearned = loan.movesLearned;
      pr.skillBar = loan.skillBar;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loan: _gone, ...rest } = pr.plainsStory!;
      pr.plainsStory = rest;
    }
    for (const c of STORY_CAST) {
      const at = this.floor(c.at[0], c.at[1]);
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: c.name, title: c.title, sprite: c.sprite, facing: c.facing ?? 1 });
      this.people.set(c.id, e);
      this.roleOf.set(e.id, c.id);
      this.homes.set(c.id, at);
    }
    // The fair: the launch sects that are schools stand at their banners as the World's school masters.
    for (const r of FAIR_RECRUITERS) {
      const sc = SCHOOLS[r.school];
      if (!sc || w.schoolMasters.has(sc.id)) continue;
      const at = this.floor(r.at[0], r.at[1]);
      w.schoolMasters.set(
        sc.id,
        this.h.add({ kind: 'npc', x: at.x, y: at.y, sprite: sc.master.sprite, name: sc.master.name, title: sc.master.title, facing: 1 }),
      );
    }
    // The ring master of the duel ring is เหล่ยกัง (the story's own person): the ring's second one steps aside.
    const ground = duelGroundOf(w);
    if (ground && ground.master >= 0) w.entities.delete(ground.master);
    // The hub rival waits beside the ring (M3).
    const rival = w.get(duelMatchOf(w)?.rivalId ?? -1);
    if (rival) {
      Object.assign(rival, this.floor(RIVAL_AT[0], RIVAL_AT[1]));
      rival.title = '◆ คู่ประลองของเจ้า · ท้าได้เลย';
    }
    this.artsSeen = -1;
    // Saved half way through a quest that has people of its own: they are back.
    const q = w.currentQuest();
    if (q?.id === PQ.masks && w.progress.quest.status === 'active') this.startMasks();
    if (q?.id === PQ.forms && w.progress.quest.status === 'active') this.startSpar();
  }

  /** Townsfolk beside the quest people: put in on the first step, after every other entity has its id. */
  private addFolk(): void {
    this.folkMade = true;
    for (const f of FOLK_NEAR) {
      const at = this.floor(f.at[0], f.at[1]);
      const e = this.h.add({ kind: 'npc', x: at.x, y: at.y, name: f.name, title: f.title, sprite: f.sprite, facing: f.facing ?? 1 });
      this.folk.set(e.id, { lines: f.lines, turn: 0 });
    }
  }

  /** The nearest open floor to a point (or the point itself). */
  private floor(x: number, y: number): { x: number; y: number } {
    const f = nearestFloor(this.h.world.map, x, y, 4);
    return f ? { x: f[0], y: f[1] } : { x, y };
  }

  // ───────────────────────── reading ─────────────────────────

  /** Whether the plains chain runs in this World. */
  get active(): boolean {
    return this.on;
  }

  /** A story person by role (for the client and the tests). */
  person(role: StoryRole): Entity | undefined {
    return this.people.get(role);
  }

  /** The mark over an NPC's head (labels): the giver of the quest on offer, whoever takes back a quest done, a recruiter. */
  markOf(e: Entity): NpcMark {
    const w = this.h.world;
    const q = w.currentQuest();
    const st = w.progress.quest.status;
    const giver = q ? this.giverOf(q) : null;
    if (q && giver === e && (st === 'available' || st === 'ready') && !(st === 'ready' && AUTO_TURN_IN.includes(q.id)))
      return st === 'available' ? 'offer' : 'ready';
    // M3 under way: the one to challenge wears the mark.
    if (this.at(PQ.duel, 'active') && e.id === (duelMatchOf(w)?.rivalId ?? -2)) return 'offer';
    const role = this.roleOf.get(e.id);
    const recruiter = role === 'tide' || role === 'host' || [...w.schoolMasters.values()].includes(e);
    return recruiter && !this.chosen() ? 'recruit' : null;
  }

  /** The person who gives (and takes back) a quest. */
  private giverOf(q: QuestDef): Entity | undefined {
    const role = QUEST_GIVER[q.id];
    return role ? this.people.get(role) : this.h.world.elder;
  }

  /** Whether the fair's choice has been made (a school joined or a choice recorded). */
  private chosen(): boolean {
    const pr = this.h.world.progress;
    return !!pr.school || !!pr.plainsStory?.sect;
  }

  private sex(): 'm' | 'f' {
    return this.h.world.progress.appearance.sex === 'f' ? 'f' : 'm';
  }

  private ix(id: string): number {
    return QUESTS.findIndex((q) => q.id === id);
  }

  /** The current quest is this one, in this state (any state when left out). */
  private at(id: string, status?: PlayerProgress['quest']['status']): boolean {
    const w = this.h.world;
    return w.currentQuest()?.id === id && (!status || w.progress.quest.status === status);
  }

  // ───────────────────────── talk ─────────────────────────

  talk(npc: Entity): boolean {
    if (!this.on) return false;
    const w = this.h.world;
    const q = w.currentQuest();
    const st = w.progress.quest.status;
    if (npc === w.elder) return this.talkElder(q, st);
    const sch = this.masterSchool(npc);
    if (sch && !this.chosen() && !this.trial && !this.trialOffered.has(sch)) {
      this.trialOffered.add(sch);
      this.dialog(npc, LINES.trialPitch, 'accept', FAIR_TRIAL.button);
      this.choice = `trial:${sch}`;
      return true;
    }
    const fk = this.folk.get(npc.id);
    if (fk) {
      const line = fk.lines[fk.turn++ % fk.lines.length]!;
      return this.dialog(npc, line.replace(' / ', ' '), null);
    }
    const role = this.roleOf.get(npc.id);
    if (!role) return false;
    // A giver of the chain's own quest: offer, remind, hand in (the World's accept and turn-in do the rest).
    if (q && QUEST_GIVER[q.id] === role) {
      if (st === 'available') return this.dialog(npc, q.offer, 'accept');
      if (st === 'active' && q.id === PQ.stones) {
        this.wangLesson(npc);
        return this.dialog(npc, LINES.f2Lesson.replace(' / ', ' '), null);
      }
      if (st === 'active') return this.dialog(npc, q.remind, null);
      return this.dialog(npc, q.done, AUTO_TURN_IN.includes(q.id) ? null : 'turnIn');
    }
    const pr = w.progress;
    switch (role) {
      case 'gao':
        return this.dialog(npc, this.past(PQ.masks) ? LINES.gaoAfter : this.at(PQ.masks, 'available') ? LINES.gaoHook : LINES.gaoIdle, null);
      case 'qiming':
        return this.dialog(npc, this.at(PQ.masks) ? q!.remind : LINES.qimingIdle, null);
      case 'leigang':
        return this.dialog(npc, LINES.duelRematch, null);
      case 'wang':
        if (this.past(PQ.bandits) && pr.movesLearned && !pr.movesLearned.includes(MIST_MOVE)) {
          // Saves from before the escort: 霧 is still owed.
          this.learn(MIST_MOVE);
          this.bark(npc.id, LINES.mist);
          return this.dialog(npc, LINES.mist.replace(' / ', ' '), null);
        }
        return this.dialog(npc, LINES.wangIdle, null);
      case 'host':
        if (this.chosen()) return this.dialog(npc, LINES.fairLater, null);
        this.dialog(npc, LINES.fairHost, 'accept', 'เดินทางลำพัง');
        this.choice = WANDERER;
        return true;
      case 'tide':
        if (pr.plainsStory?.sect === TIDE_SECT) return this.dialog(npc, LINES.tideMember, null);
        if (this.chosen()) return this.dialog(npc, LINES.tideTaken, null);
        this.dialog(npc, LINES.tidePitch, 'accept', 'เข้าสำนักคลื่นไผ่');
        this.choice = TIDE_SECT;
        return true;
      case 'lu':
        return this.dialog(npc, LINES.luIdle, null);
    }
  }

  /** F2: ครูหวัง's lesson: the first qinggong art (taught for its price) and the `talk` objective. */
  private wangLesson(wang: Entity | undefined): void {
    const w = this.h.world;
    const r = ROUTES.stones!;
    if ((w.progress.qinggong[r.needsArt] ?? 0) < 1) w.apply({ t: 'qinggongLearn', id: r.needsArt });
    if (wang) this.bark(wang.id, LINES.f2Lesson);
    this.bump('talk', 'wang');
  }

  private talkElder(q: QuestDef | undefined, st: PlayerProgress['quest']['status']): boolean {
    const w = this.h.world;
    if (!q) return false;
    if (q.id === PQ.keeper) {
      // His meeting plays as a scene where cutscenes run (scene.ts finds it through `meetElder`).
      if (w.story.enabled && !w.progress.scenes.includes('plains-keeper')) return false;
      this.dialog(w.elder, q.offer, null);
      this.finishKeeper();
      return true;
    }
    const role = QUEST_GIVER[q.id];
    if (role) {
      const who = this.people.get(role);
      return this.dialog(w.elder, `ไปหา${who?.name ?? ''}${q.giver?.where ?? ''}เถิด`, null);
    }
    if (st === 'available') return this.dialog(w.elder, q.offer, 'accept');
    if (st === 'active') {
      // M6: once the hall is seen, the choice may wait (§6 M6: the next chapter starts either way).
      if (q.id === PQ.banners && (w.progress.quest.progress[0] ?? 0) >= 1 && !this.chosen()) {
        this.dialog(w.elder, `${q.remind} · ${LINES.fairLater}`, 'turnIn', 'ไว้เลือกทีหลัง');
        this.deferOffered = true;
        return true;
      }
      return this.dialog(w.elder, q.remind, null);
    }
    if (AUTO_TURN_IN.includes(q.id)) return this.dialog(w.elder, q.done, null);
    return this.dialog(w.elder, q.done, 'turnIn');
  }

  private dialog(npc: Entity, text: string, action: 'accept' | 'turnIn' | null, label?: string): true {
    this.choice = null;
    this.deferOffered = false;
    this.h.emit({ type: 'dialog', npcId: npc.id, text: bySex(text, this.sex()), action, ...(label ? { label } : {}) });
    return true;
  }

  // ───────────────────────── commands ─────────────────────────

  /** The fair's choices and the elder's "later" ride on the dialog's one button. */
  refuse(cmd: PlayerCommand): boolean {
    if (!this.on) return false;
    if (cmd.t === 'questAccept' && this.choice?.startsWith('trial:')) {
      const id = this.choice.slice(6);
      this.choice = null;
      this.startTrial(id);
      return true;
    }
    if (cmd.t === 'questAccept' && this.choice) {
      const pick = this.choice as 'bamboo-tide' | 'wanderer';
      this.choice = null;
      if (this.chosen()) return true;
      this.record(pick);
      return true;
    }
    if (cmd.t === 'questTurnIn' && this.deferOffered && this.at(PQ.banners, 'active')) {
      this.deferOffered = false;
      this.record('later');
      return true;
    }
    return false;
  }

  /** The fair's choice recorded; M6's `sect` objective met. */
  private record(sect: 'bamboo-tide' | 'wanderer' | 'later'): void {
    const w = this.h.world;
    const pr = w.progress;
    pr.plainsStory = { ...pr.plainsStory, sect };
    const host = this.people.get(sect === TIDE_SECT ? 'tide' : 'host');
    if (sect === TIDE_SECT) {
      this.h.log('sys', 'ลงชื่อเป็นศิษย์สำนักคลื่นไผ่แล้ว · สำนักจะเปิดรับเต็มตัวเร็วๆ นี้');
      if (host) this.bark(host.id, LINES.tideMember);
    } else if (sect === WANDERER) {
      this.h.log('sys', 'เลือกเดินทางลำพัง');
      if (host) this.bark(host.id, LINES.fairWanderer);
    } else this.h.notice(LINES.fairLater);
    this.h.emit({ type: 'progressChanged' });
    this.bump('sect');
  }

  // ───────────────────────── events ─────────────────────────

  observe(ev: WorldEvent): void {
    if (!this.on) return;
    const w = this.h.world;
    const p = w.player;
    switch (ev.type) {
      case 'dialog': {
        // Another's dialog: the fair's last offer is off the screen.
        const role = this.roleOf.get(ev.npcId);
        if (role !== 'host' && role !== 'tide' && !this.masterSchool(w.get(ev.npcId))) this.choice = null;
        if (ev.npcId !== w.elder.id) this.deferOffered = false;
        break;
      }
      case 'scene':
        if (ev.id === 'plains-opening' && ev.phase === 'start') this.gate();
        // Skipping the opening skips the wake that follows it too (the recap card covers both).
        if (ev.id === 'plains-opening' && ev.phase === 'end' && ev.skipped) this.skipWake = true;
        if (ev.id === 'plains-keeper' && ev.phase === 'end') this.finishKeeper();
        break;
      case 'meditate':
        if (ev.on && this.near(p, 'stele')) this.bump('meditate', 'stele');
        break;
      case 'hit':
        this.fixes.observe(ev);
        this.onHit(ev.srcId, ev.tgtId, ev.outcome);
        break;
      case 'pvpDuel':
        if (ev.phase === 'result' && ev.winner === p.id) {
          this.bump('duelWin');
          const rival = w.get(duelMatchOf(w)?.rivalId ?? -1);
          if (rival && this.at(PQ.duel)) this.bark(rival.id, LINES.duelWon);
        } else if (ev.phase === 'result' && this.at(PQ.duel, 'active')) {
          const lg = this.people.get('leigang');
          if (lg) this.bark(lg.id, LINES.duelLost);
        }
        break;
      case 'joinedSchool':
        this.endTrialOnJoin(ev.school);
        this.bump('sect');
        break;
      case 'quest':
        this.onQuest(ev.status, ev.questId);
        break;
      case 'stance':
        if (this.chief && ev.id === this.chief.id) this.qimingCall(LINES.chiefGuard);
        break;
      case 'skill':
        if (this.trial && ev.srcId === p.id) this.trialMoves.add(ev.skillId);
        break;
      case 'castStart':
        if (this.chief && ev.srcId === this.chief.id && getSkill(ev.skillId).kind === 'break') this.qimingCall(LINES.chiefCharge);
        break;
      case 'death':
        if (this.chief && ev.id === this.chief.id && this.at(PQ.masks)) {
          this.bark(this.chief.id, LINES.chiefFalls);
          this.bark(p.id, LINES.tassel, 4.2);
          this.tasselDue = true;
          this.dropGear();
        }
        break;
      default:
        break;
    }
  }

  private onQuest(status: 'accepted' | 'progress' | 'ready' | 'completed', id: string): void {
    const w = this.h.world;
    const pr = w.progress;
    if (status === 'accepted') {
      for (const m of MOVES_ON_ACCEPT[id] ?? []) this.learn(m);
      if (id === PQ.forms) this.startSpar();
      if (id === PQ.stones) this.wangLesson(this.people.get('wang'));
      if (id === PQ.duel) {
        const rival = w.get(duelMatchOf(w)?.rivalId ?? -1);
        if (rival) this.bark(rival.id, LINES.duelRival, 1);
        this.late = `${LINES.duelHint} · ${LINES.f1Hint}`;
      }
      if (id === PQ.masks) {
        this.startMasks();
        this.late = this.chosen() && pr.plainsStory?.sect !== 'later' ? LINES.masksFirst : LINES.sectReminder;
      }
      this.guideTo(this.place(id));
    } else if (status === 'ready') {
      if (AUTO_TURN_IN.includes(id)) this.queue.push({ t: 'questTurnIn' });
      else {
        const q = QUESTS.find((x) => x.id === id);
        this.late = `ภารกิจสำเร็จ · กลับไปหา${q?.giver?.name ?? 'ผู้เฒ่าหลิว'}`;
        this.guideTo(q && this.giverOf(q));
      }
      if (id === PQ.forms) this.endSpar();
    } else if (status === 'completed') {
      for (const m of MOVES_ON_DONE[id] ?? []) this.learn(m);
      this.afterDone(id);
    }
  }

  /** What follows a quest handed in: its people's lines and the marker to the next giver. */
  private afterDone(id: string): void {
    const w = this.h.world;
    const say = (role: StoryRole, text: string, after = 0) => {
      const e = this.people.get(role);
      if (e) this.bark(e.id, text, after);
    };
    if (id === PQ.forms) {
      say('qiming', LINES.forms3Won);
      say('qiming', LINES.formsOdd, 5);
    }
    if (id === PQ.forms) this.late = LINES.sitHint;
    if (id === PQ.duel) say('leigang', LINES.duelReed);
    if (id === PQ.stones) this.bark(w.player.id, LINES.f2Done);
    if (id === PQ.bandits) this.h.log('sys', LINES.sectWhy.replace(' / ', ' · '));
    if (id === PQ.masks) {
      w.grantTitle(FIRST_TITLE);
      say('leigang', LINES.firstTitle, 3);
    }
    if (id === PQ.banners) {
      const sect = w.progress.plainsStory?.sect;
      if (sect !== 'later' && sect !== WANDERER) say('host', LINES.fairChosen);
      say('gao', LINES.gaoHook, 3);
    }
    if (id === PQ.masks) {
      const qm = this.people.get('qiming');
      const home = this.homes.get('qiming');
      if (qm && home) Object.assign(qm, home);
    }
    const next = w.currentQuest();
    if (next) this.guideTo(this.giverOf(next));
  }

  /** M1 handed in: the meeting was the quest. */
  private finishKeeper(): void {
    const w = this.h.world;
    const pr = w.progress;
    if (!this.at(PQ.keeper)) return;
    pr.quest = { index: this.ix(PQ.keeper) + 1, status: 'available', progress: [] };
    this.h.log('sys', 'ส่งภารกิจ ผู้เฝ้าศิลา');
    this.h.emit({ type: 'quest', status: 'completed', questId: PQ.keeper });
    this.h.emit({ type: 'progressChanged' });
  }

  /** A new character of the plains opening learns กระบี่ไผ่ห่มหมอก a move at a time. */
  private gate(): void {
    const pr = this.h.world.progress;
    if (pr.movesLearned || pr.quest.index > 0 || pr.activeArt !== STARTER_ART) return;
    startMoveGate(pr);
    this.h.emit({ type: 'progressChanged' });
  }

  private learn(id: string): void {
    const pr = this.h.world.progress;
    if (!learnMove(pr, id)) return;
    const s = getSkill(id);
    this.h.emit({ type: 'moveLearned', skillId: id });
    this.h.log('sys', `ได้รับกระบวนท่า ${s.glyph} ${s.name}`);
    this.late = `ได้รับกระบวนท่าใหม่ ${s.glyph} ${s.name}`;
    this.h.emit({ type: 'progressChanged' });
  }

  /** Counts an objective the World does not (as `World.questEvent` counts the fights). */
  private bump(type: ObjectiveType, target?: string): void {
    const w = this.h.world;
    const q = w.currentQuest();
    const qs = w.progress.quest;
    if (!q || qs.status !== 'active') return;
    const r = advanceObjectives(q, qs.progress, type, target, { loose: true });
    if (r === 'ready') {
      qs.status = 'ready';
      this.h.emit({ type: 'quest', status: 'ready', questId: q.id });
    } else if (r) this.h.emit({ type: 'quest', status: 'progress', questId: q.id });
  }

  // ───────────────────────── M2: the spar ─────────────────────────

  private startSpar(): void {
    const w = this.h.world;
    if (this.spar && w.entities.has(this.spar.id)) return;
    const qm = this.people.get('qiming');
    const home = this.homes.get('qiming');
    if (!qm || !home) return;
    // ฉีหมิง steps out as the sparring partner (the same man, as a fighter), and back in when it is done.
    w.entities.delete(qm.id);
    const m = this.h.spawnMonster(SPARRER, home.x, home.y);
    Object.assign(m, { name: qm.name, title: 'ซ้อมสามกระบวนท่า' });
    this.spar = m;
    this.misses = 0;
    this.hinted = -1;
    this.stageSaid = -1;
    this.sparT = 0;
  }

  private endSpar(): void {
    const w = this.h.world;
    const m = this.spar;
    this.spar = null;
    if (m) {
      w.entities.delete(m.id);
      if (w.player.targetId === m.id) w.player.targetId = null;
      this.h.input.engage = false;
    }
    const qm = this.people.get('qiming');
    if (qm) w.entities.set(qm.id, qm);
  }

  /** The duel under way (0 block, 1 break his guard, 2 cut his charge), from the objectives left. */
  private sparStage(): number {
    const w = this.h.world;
    const q = w.currentQuest();
    if (!q) return 0;
    const i = q.objectives.findIndex((o, k) => (w.progress.quest.progress[k] ?? 0) < o.count);
    return i < 0 ? 3 : i;
  }

  private stepSpar(dt: number): void {
    const w = this.h.world;
    const m = this.spar;
    if (!m) return;
    if (!this.at(PQ.forms, 'active')) return this.endSpar();
    const p = w.player;
    const stage = this.sparStage();
    if (stage !== this.stageSaid && dist(p, m) < 12) {
      const said = [LINES.forms1, LINES.forms2, LINES.forms3][stage];
      if (this.stageSaid >= 0) this.bark(m.id, stage === 1 ? LINES.forms1Won : LINES.forms2Won);
      if (said) this.bark(m.id, said, this.stageSaid >= 0 ? 2.6 : 0);
      if (this.stageSaid < 0) this.bark(m.id, LINES.formsHello, 0);
      this.stageSaid = stage;
      this.misses = 0;
    }
    // The forms count in order: what was done ahead of its turn (a block in the first duel is the first form's work,
    // but a guard-break before it is not) does not count until its turn comes.
    const qs = w.progress.quest;
    const qd = w.currentQuest();
    if (qd && dist(p, m) < 14) qd.objectives.forEach((_, k) => { if (k > stage && (qs.progress[k] ?? 0) > 0) qs.progress[k] = 0; });
    // He engages as soon as the hero is near (a bout, not a wait), and he does not give up in the ring's safe circle.
    if (m.brain && dist(p, m) < SPAR_RHYTHM.engage && !p.dead && m.brain.state !== 'chase') {
      m.brain.state = 'chase';
      m.brain.foeId = p.id;
      m.targetId = p.id;
    }
    // Neither falls: the bout starts over instead.
    if (!p.dead && p.hp < p.maxHp * 0.25) {
      p.hp = p.maxHp;
      m.hp = m.maxHp;
      this.bark(m.id, LINES.formsFail);
    }
    if (m.hp < m.maxHp * 0.3) m.hp = m.maxHp;
    if (m.dead) Object.assign(m, { dead: false, deadT: 0, hp: m.maxHp });
    // He leads each duel: in the second he holds his guard up, in the third he charges his break.
    this.sparT -= dt;
    const fighting = m.brain?.state === 'chase';
    if (!fighting || this.sparT > 0 || m.cast || m.stunT > 0 || dist(p, m) > 3.2) return;
    if (stage === 0) {
      // A telegraphed slash on a steady beat: the wind-up is the cue to guard.
      m.blocking = false;
      w.useSkill(m, 'bandit-slash', p);
      this.sparT = SPAR_RHYTHM.slash;
    } else if (stage === 1) {
      if (!m.blocking) {
        m.blocking = true;
        m.brain!.blockT = 2.6;
        this.h.emit({ type: 'stance', id: m.id });
      }
      this.sparT = SPAR_RHYTHM.guard;
    } else if (stage === 2) {
      m.blocking = false;
      w.useSkill(m, 'bandit-crush', p);
      this.sparT = SPAR_RHYTHM.crush;
    }
  }

  private onHit(src: number, tgt: number, outcome: string): void {
    const w = this.h.world;
    const p = w.player;
    if (this.chief && tgt === this.chief.id && src === p.id) {
      if (outcome === 'guardBreak') this.openGate(0, this.chief);
      if (outcome === 'interrupt') this.openGate(1, this.chief);
    }
    const m = this.spar;
    if (m && (src === m.id || tgt === m.id)) {
      const stage = this.sparStage();
      const miss =
        (stage === 0 && src === m.id && tgt === p.id && outcome === 'hit') ||
        (stage === 1 && src === p.id && outcome === 'blocked') ||
        (stage === 2 && src === m.id && tgt === p.id && (outcome === 'guardBreak' || outcome === 'hit'));
      if (miss && ++this.misses >= 2 && this.hinted !== stage) {
        this.hinted = stage;
        this.h.notice(LINES.formsHints[stage] ?? '');
      }
    }
  }

  // ───────────────────────── M7: the masked men ─────────────────────────

  private startMasks(): void {
    const w = this.h.world;
    if (this.chief && w.entities.has(this.chief.id)) return;
    for (const [x, y] of MASK_SPOTS) {
      const at = this.floor(x, y);
      this.h.spawnMonster(MASKED, at.x, at.y);
    }
    const c = this.floor(CHIEF_SPOT[0], CHIEF_SPOT[1]);
    this.chief = this.h.spawnMonster(MASKED_CHIEF, c.x, c.y);
    this.called = false;
    this.chiefGate[0] = this.chiefGate[1] = false;
    this.pin[0] = this.pin[1] = 0;
    this.chiefPhase = 0;
    this.chiefGuardT = this.chiefChargeT = 0;
    const qm = this.people.get('qiming');
    if (qm) Object.assign(qm, this.floor(QIMING_M7[0], QIMING_M7[1]));
  }

  private stepMasks(dt: number): void {
    const w = this.h.world;
    const c = this.chief;
    if (!c || !this.at(PQ.masks, 'active')) return;
    const p = w.player;
    if (!c.dead && dist(p, c) < 12) this.once('chiefSeen', () => this.bark(c.id, LINES.chiefSeen));
    if (!c.dead && !this.called && c.hp < c.maxHp * 0.5) {
      this.called = true;
      this.bark(c.id, LINES.chiefCalls);
      for (const [dx, dy] of [
        [-3, 2],
        [3, 2],
      ] as const) {
        const at = this.floor(c.x + dx, c.y + dy);
        this.h.spawnMonster(MASKED, at.x, at.y);
      }
    }
    if (!c.dead) this.stepChief(dt, c, p);
    // Fallen while the masks were hunted: ฉีหมิง calls the retreat once the player is up again.
    if (this.wasDead && !p.dead) this.qimingCall(LINES.masksFail, true);
    this.wasDead = p.dead;
  }

  /**
   * The chief is a fight with three parts: his guard (break it with a ท่าทำลาย: he does not fall below 66 % until
   * it is broken), his charged break (cut it with an attack: he does not fall below 33 % until it is cut), and then
   * a rage. A part the hero has no answer to opens after `CHIEF.gateSeconds` of holding him there.
   */
  private stepChief(dt: number, c: Entity, p: Entity): void {
    const w = this.h.world;
    const frac = c.hp / c.maxHp;
    for (let i = 0; i < 2; i++) {
      if (this.chiefGate[i]) continue;
      const floor = i === 0 ? CHIEF.guardFloor : CHIEF.chargeFloor;
      if (c.hp <= c.maxHp * floor + 1e-6) {
        c.hp = c.maxHp * floor;
        this.pin[i] += dt;
        if (this.pin[i] >= CHIEF.gateSeconds) this.openGate(i, c);
      }
      break;
    }
    const phase = frac > CHIEF.guardFloor ? 1 : frac > CHIEF.chargeFloor ? 2 : 3;
    if (phase !== this.chiefPhase) {
      this.chiefPhase = phase;
      const line = [LINES.chiefPhase1, LINES.chiefPhase2, LINES.chiefPhase3][phase - 1]!;
      this.h.notice(line);
      if (phase === 3) c.speed *= CHIEF.rageSpeed;
    }
    if (p.dead || dist(p, c) > 5 || c.cast || c.stunT > 0) return;
    if (c.brain && c.brain.state !== 'chase') {
      c.brain.state = 'chase';
      c.brain.foeId = p.id;
      c.targetId = p.id;
    }
    const rage = phase === 3 ? 0.7 : 1;
    this.chiefGuardT -= dt;
    this.chiefChargeT -= dt;
    if (phase !== 2 && this.chiefGuardT <= 0 && !c.blocking) {
      c.blocking = true;
      if (c.brain) c.brain.blockT = 2.4;
      this.h.emit({ type: 'stance', id: c.id });
      this.chiefGuardT = CHIEF.guardEvery * rage;
    } else if (phase >= 2 && this.chiefChargeT <= 0) {
      c.blocking = false;
      w.useSkill(c, 'bandit-crush', p);
      this.chiefChargeT = CHIEF.chargeEvery * rage;
    }
  }

  private openGate(i: 0 | 1 | number, c: Entity): void {
    if (this.chiefGate[i as 0 | 1]) return;
    this.chiefGate[i as 0 | 1] = true;
    this.bark(c.id, LINES.chiefGateOpen);
  }

  private qimingCall(text: string, always = false): void {
    if (!always && this.callT > 0) return;
    const qm = this.people.get('qiming');
    if (!qm || dist(qm, this.h.world.player) > 24) return;
    this.callT = 6;
    this.bark(qm.id, text);
  }

  // ───────────────────────── step ─────────────────────────

  step(dt: number): void {
    if (!this.on) return;
    const w = this.h.world;
    const p = w.player;
    const pr = w.progress;
    if (!this.folkMade) this.addFolk();
    if (this.late) {
      this.h.notice(this.late);
      this.late = null;
    }
    if (!w.story.active) {
      // Hand-ins and the like, after the event that asked for them.
      while (this.queue.length) w.apply(this.queue.shift()!);
    }
    if (this.skipWake && w.scene?.id === 'plains-wake') {
      this.skipWake = false;
      w.apply({ t: 'sceneSkip' });
    }
    this.callT = Math.max(0, this.callT - dt);
    this.stepSpoken(dt);
    this.stepLife(dt);
    // Every art other than the starter's is learned whole (only กระบี่ไผ่ห่มหมอก is taught a move at a time).
    if (pr.movesLearned && pr.arts.length !== this.artsSeen) {
      this.artsSeen = pr.arts.length;
      for (const a of pr.arts)
        if (a !== STARTER_ART) for (const id of getArt(a).skills) if (!pr.movesLearned.includes(id)) pr.movesLearned.push(id);
    }
    const q = w.currentQuest();
    if (!q || p.dead) {
      this.wasDead = p.dead;
      return;
    }
    const st = pr.quest.status;
    // The quests that teach a mechanic are fought by hand: auto-fight is off for them.
    if ((q.id === PQ.wolves || q.id === PQ.bandits || q.id === PQ.masks || q.id === PQ.forms) && st === 'active' && w.auto.on) {
      w.auto.on = false;
      this.h.input.engage = false;
      this.once(`autoOff-${q.id}`, () => this.h.notice(LINES.autoOff));
    }
    // W6: the elder turns and calls when the hero comes near.
    if (q.id === PQ.keeper && !w.story.active && dist(p, w.elder) < 14) this.once('elderCall', () => this.bark(w.elder.id, LINES.elderCall));
    if (q.id === PQ.forms && st !== 'ready') {
      const qm = this.spar ?? this.people.get('qiming');
      if (qm && dist(p, qm) < 10 && st === 'available') this.once('formsHello', () => this.bark(qm.id, LINES.formsHello));
    }
    if (q.id === PQ.wolves && st === 'active') {
      const tr = pr.training;
      if ((tr?.kind === 'neigong' && tr.id === 'blood-oath') || (pr.neigong['blood-oath'] ?? 0) >= 2) this.bump('neigong', 'blood-oath');
    }
    if (q.id === PQ.wolves && st === 'active' && this.near(p, 'woodEdge')) this.once('woodEdge', () => this.bark(p.id, LINES.woodEdge));
    if (q.id === PQ.banners && st === 'active' && this.near(p, 'hall')) this.bump('visit', 'hall');
    if (q.id === PQ.banners && st === 'active' && this.chosen()) this.bump('sect');
    this.stepSpar(dt);
    this.fixes.step(dt);
    this.stepMasks(dt);
    this.stepQiming(dt);
  }

  /** Whole-chain upkeep that does not depend on the current quest: the tassel, 颯, the fair's demo, the folk's chatter. */
  private stepLife(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    const pr = w.progress;
    if (this.tasselDue && !pr.inventory.includes(TASSEL_ID)) {
      if (pr.inventory.length >= BAG_SIZE) this.once('tasselFull', () => this.h.notice('กระเป๋าเต็ม · เก็บพู่กระบี่ไม่ได้ ทิ้งของสักชิ้นแล้วมาเก็บ'));
      else {
        pr.inventory.push(TASSEL_ID);
        this.tasselDue = false;
        this.h.log('sys', 'ได้รับ พู่กระบี่สีแดง');
        this.h.notice('ได้รับ พู่กระบี่สีแดง (ช่องสมบัติ)');
        this.h.emit({ type: 'progressChanged' });
      }
    } else if (this.tasselDue) this.tasselDue = false;
    // 颯 is the prize of the first win over the second คู่ฝึก (M8's hand-in gives it too, for one who skips the ladder).
    if (pr.movesLearned && !pr.movesLearned.includes(GLIDE_MOVE) && pr.sparring?.beaten.includes(GLIDE_TIER)) {
      this.learn(GLIDE_MOVE);
      const lg = this.people.get('leigang');
      if (lg) this.bark(lg.id, LINES.gliderWon);
    }
    this.stepDemo(dt, p);
    this.stepTrial(dt);
  }

  /** The school whose master this is (the fair's recruiters), if any. */
  private masterSchool(e: Entity | undefined): string | null {
    if (!e) return null;
    for (const [id, m] of this.h.world.schoolMasters) if (m === e) return id;
    return null;
  }

  /** The trial bout: the hero borrows the school's first art and fights a sparring lad beside the recruiter. */
  private startTrial(school: string): void {
    const w = this.h.world;
    const pr = w.progress;
    const master = w.schoolMasters.get(school);
    const sc = SCHOOLS[school];
    const p = w.player;
    if (!master || !sc || this.trial || p.dead || w.duel || this.spar || this.chosen()) return;
    const artId = sc.arts[0]!;
    this.trial = {
      school,
      t: 0,
      saved: { arts: [...pr.arts], activeArt: pr.activeArt, movesLearned: pr.movesLearned && [...pr.movesLearned], skillBar: pr.skillBar && [...pr.skillBar] },
      foe: null as unknown as Entity,
    };
    pr.plainsStory = {
      ...pr.plainsStory,
      loan: {
        arts: [...pr.arts],
        activeArt: pr.activeArt,
        ...(pr.movesLearned ? { movesLearned: [...pr.movesLearned] } : {}),
        ...(pr.skillBar ? { skillBar: [...pr.skillBar] } : {}),
      },
    };
    if (!pr.arts.includes(artId)) pr.arts.push(artId);
    if (pr.movesLearned) for (const id of getArt(artId).skills) if (!pr.movesLearned.includes(id)) pr.movesLearned.push(id);
    pr.skillBar = undefined;
    w.apply({ t: 'setArt', id: artId });
    const at = this.floor(master.x + 5, master.y + 3.5);
    this.trialMoves.clear();
    this.trialSwingT = 2;
    this.trialGuardT = FAIR_TRIAL.guard;
    this.trialCrushT = FAIR_TRIAL.crush;
    this.trialTold = 0;
    const foe = this.h.spawnMonster(FAIR_TRIAL.foe, at.x, at.y);
    Object.assign(foe, { name: 'ศิษย์ซ้อมวิชา', title: `ลองวิชา${sc.name} · ใช้ท่าให้ครบสามท่า`, maxHp: FAIR_TRIAL.foeHp, hp: FAIR_TRIAL.foeHp });
    this.trial.foe = foe;
    this.bark(master.id, LINES.trialGo);
    this.late = LINES.trialTask;
    this.h.emit({ type: 'progressChanged' });
  }

  /** The chief's drop: a rare weapon of the hero's kind with rolled stats, straight into the bag. */
  private dropGear(): void {
    const w = this.h.world;
    const pr = w.progress;
    if (pr.inventory.length >= BAG_SIZE) return this.h.notice('กระเป๋าเต็ม · อาวุธของหัวหน้าหน้ากากไม้หล่นหาย');
    const base = isArmed(getArt(pr.activeArt)) ? 'azure-sword' : 'iron-claw';
    if (!ITEM_LIST.some((i) => i.id === base)) return;
    void getItem;
    const id = `roll-${pr.nextRoll++}`;
    pr.rolls[id] = rollItem(base, CHIEF_DROP.ilv, CHIEF_DROP.rarity, this.h.random);
    pr.inventory.push(id);
    this.h.emit({ type: 'loot', id: this.chief?.id ?? -1, item: id });
    this.h.emit({ type: 'progressChanged' });
    this.h.log('sys', LINES.gearDrop);
    this.h.notice(LINES.gearDrop);
  }

  private stepTrial(dt: number): void {
    const tr = this.trial;
    if (!tr) return;
    const w = this.h.world;
    const p = w.player;
    tr.t += dt;
    const f = tr.foe;
    if (f.dead) Object.assign(f, { dead: false, deadT: 0, hp: f.maxHp * FAIR_TRIAL.floorHp });
    const done = this.trialMoves.size >= FAIR_TRIAL.moves;
    // He cannot fall until the lent moves have been tried: three different ones, then he goes down.
    if (!done && f.hp < f.maxHp * FAIR_TRIAL.floorHp) f.hp = f.maxHp * FAIR_TRIAL.floorHp;
    if (this.trialMoves.size !== this.trialTold) {
      this.trialTold = this.trialMoves.size;
      if (!done) this.h.notice(`ท่าที่ลองแล้ว ${this.trialTold}/${FAIR_TRIAL.moves} · ${LINES.trialMore}`);
      else this.h.notice('ครบสามท่าแล้ว · ล้มเขาให้ได้');
    }
    const master = w.schoolMasters.get(tr.school);
    const won = done && f.hp < f.maxHp * FAIR_TRIAL.winAt;
    const hurt = !p.dead && p.hp < p.maxHp * FAIR_TRIAL.hurtAt;
    if (won || hurt || p.dead || tr.t > FAIR_TRIAL.maxSeconds || dist(p, f) > FAIR_TRIAL.stray) return this.endTrial(won);
    if (master) this.h.face(master, p);
    // The lad fights on a rhythm: a swing, a guard to break, a charged break to cut.
    if (f.brain && dist(p, f) < 8 && f.brain.state !== 'chase') {
      f.brain.state = 'chase';
      f.brain.foeId = p.id;
      f.targetId = p.id;
    }
    this.trialSwingT -= dt;
    this.trialGuardT -= dt;
    this.trialCrushT -= dt;
    if (f.cast || f.stunT > 0 || dist(p, f) > 3.2) return;
    if (this.trialCrushT <= 0) {
      f.blocking = false;
      w.useSkill(f, 'bandit-crush', p);
      this.trialCrushT = FAIR_TRIAL.crush;
      this.trialSwingT = FAIR_TRIAL.swing;
    } else if (this.trialGuardT <= 0 && !f.blocking) {
      f.blocking = true;
      if (f.brain) f.brain.blockT = 2.4;
      this.h.emit({ type: 'stance', id: f.id });
      this.trialGuardT = FAIR_TRIAL.guard;
    } else if (this.trialSwingT <= 0) {
      f.blocking = false;
      w.useSkill(f, 'bandit-slash', p);
      this.trialSwingT = FAIR_TRIAL.swing;
    }
  }

  /** A school joined while the trial runs: close the trial now, give the loan back but keep what the join gained (never wipe it). */
  private endTrialOnJoin(school: string): void {
    const tr = this.trial;
    if (!tr) return;
    const w = this.h.world;
    const pr = w.progress;
    const first = SCHOOLS[school]?.arts[0];
    this.trial = null;
    const arts = [...tr.saved.arts];
    if (first && !arts.includes(first)) arts.push(first);
    pr.arts = arts;
    if (tr.saved.movesLearned) {
      const mv = [...tr.saved.movesLearned];
      if (first) for (const id of getArt(first).skills) if (!mv.includes(id)) mv.push(id);
      pr.movesLearned = mv;
    } else pr.movesLearned = undefined;
    pr.skillBar = tr.saved.skillBar;
    if (first && pr.activeArt !== first) w.apply({ t: 'setArt', id: first });
    this.artsSeen = -1;
    if (pr.plainsStory) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loan: _gone, ...rest } = pr.plainsStory;
      pr.plainsStory = rest;
    }
    w.entities.delete(tr.foe.id);
    if (w.player.targetId === tr.foe.id) w.player.targetId = null;
    this.h.input.engage = false;
    this.h.emit({ type: 'progressChanged' });
  }

  /** Gives the art and the moves back, removes the lad; waits a tick if the hero is mid-cast. */
  private endTrial(won: boolean): void {
    const tr = this.trial;
    if (!tr) return;
    const w = this.h.world;
    const pr = w.progress;
    const p = w.player;
    if (p.cast) return;
    this.trial = null;
    if (pr.activeArt !== tr.saved.activeArt) w.apply({ t: 'setArt', id: tr.saved.activeArt });
    pr.arts = tr.saved.arts;
    pr.movesLearned = tr.saved.movesLearned;
    pr.skillBar = tr.saved.skillBar;
    this.artsSeen = -1;
    if (pr.plainsStory) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { loan: _gone, ...rest } = pr.plainsStory;
      pr.plainsStory = rest;
    }
    w.entities.delete(tr.foe.id);
    if (p.targetId === tr.foe.id) p.targetId = null;
    this.h.input.engage = false;
    if (!p.dead && p.hp < p.maxHp * 0.5) p.hp = p.maxHp * 0.5;
    const master = w.schoolMasters.get(tr.school);
    if (master) this.bark(master.id, won ? LINES.trialWon : LINES.trialEnd);
    this.h.emit({ type: 'progressChanged' });
  }

  /** The fair's demo bouts: a pair of recruiters trade blows while the hero is near. */
  private stepDemo(dt: number, p: Entity): void {
    this.demoT -= dt;
    if (this.demoT > 0 || p.dead) return;
    const w = this.h.world;
    const pairs = FAIR_DEMO.pairs.map(([a, b]) => [this.recruiter(a), this.recruiter(b)] as const);
    const near = pairs.filter(([a, b]) => a && b && Math.min(dist(p, a), dist(p, b)) < FAIR_DEMO.near);
    if (!near.length) return;
    this.demoT = FAIR_DEMO.blow;
    const turn = this.demoTurn++;
    for (const [a, b] of near) {
      const [src, tgt] = turn % 2 === 0 ? [a!, b!] : [b!, a!];
      this.h.face(src, tgt);
      src.swingT = 0.32;
      this.h.emit({ type: 'basic', srcId: src.id, tgtId: tgt.id, step: turn % 3 });
      if (turn === 0) this.once(`demo-${src.id}`, () => this.bark(src.id, FAIR_DEMO.line));
    }
    void w;
  }

  private recruiter(id: string): Entity | undefined {
    if (id === 'tide') return this.people.get('tide');
    const sc = SCHOOLS[id];
    return sc ? this.h.world.schoolMasters.get(sc.id) : undefined;
  }

  /** ฉีหมิง fights beside the hero in M7: into reach of the nearest masked man, a blow now and then, never the last one. */
  private stepQiming(dt: number): void {
    const w = this.h.world;
    const p = w.player;
    const qm = this.people.get('qiming');
    if (!qm || !this.at(PQ.masks, 'active') || p.dead || this.spar) return;
    this.qimingT -= dt;
    let foe: Entity | null = null;
    let bd: number = QIMING_FIGHT.sight;
    for (const o of w.entities.values()) {
      if (o.kind !== 'monster' || o.dead || (o.brain?.defId !== MASKED && o.brain?.defId !== MASKED_CHIEF)) continue;
      if (dist(p, o) > QIMING_FIGHT.sight + 8) continue;
      const d = dist(qm, o);
      if (d < bd) [foe, bd] = [o, d];
    }
    if (!foe) return;
    qm.hp = qm.maxHp;
    qm.combatT = 2;
    this.h.face(qm, foe);
    if (bd > QIMING_FIGHT.reach) {
      const s = Math.min(bd - QIMING_FIGHT.reach * 0.8, qm.speed * 1.4 * dt);
      qm.moving = this.h.move(qm, ((foe.x - qm.x) / bd) * s, ((foe.y - qm.y) / bd) * s);
    } else if (this.qimingT <= 0 && foe.hp > foe.maxHp * QIMING_FIGHT.leaveHp) {
      this.qimingT = QIMING_FIGHT.blow;
      qm.swingT = 0.32;
      Object.assign(qm, { atk: QIMING_FIGHT.atk, hit: 10, crit: 0.05 });
      this.h.emit({ type: 'basic', srcId: qm.id, tgtId: foe.id, step: Math.floor(w.time * 3) % 3 });
      this.h.hit(qm, foe, 1, 'attack', 'basic');
      this.once('qimingFight', () => this.bark(qm.id, LINES.qimingFights));
    }
  }

  private once(key: string, f: () => void): void {
    if (this.said.has(key)) return;
    this.said.add(key);
    f();
  }

  /** Says a line over someone, a bubble at a time (`/` splits it), after `after` seconds. */
  private bark(id: number, text: string, after = 0): void {
    let t = after;
    for (const part of bySex(text, this.sex()).split(' / ')) {
      this.spoken.push({ id, text: part, t });
      t += sayFor(part);
    }
  }

  private stepSpoken(dt: number): void {
    for (let i = 0; i < this.spoken.length; ) {
      const s = this.spoken[i]!;
      s.t -= dt;
      if (s.t > 0) {
        i++;
        continue;
      }
      this.spoken.splice(i, 1);
      if (this.h.world.get(s.id)) this.h.emit({ type: 'bark', id: s.id, text: s.text });
    }
  }

  // ───────────────────────── places and the marker ─────────────────────────

  private near(e: { x: number; y: number }, place: string): boolean {
    const pl = STORY_PLACES[place];
    return !!pl && Math.hypot(e.x - pl.at[0], e.y - pl.at[1]) < pl.r;
  }

  /** Where a quest's work is: its place, or (M3) the rival, (M2) the spar. */
  private place(id: string): { x: number; y: number } | undefined {
    const w = this.h.world;
    if (id === PQ.duel) return w.get(duelMatchOf(w)?.rivalId ?? -1);
    if (id === PQ.forms) return this.spar ?? undefined;
    const pl = STORY_PLACES[QUEST_PLACE[id] ?? ''];
    return pl ? { x: pl.at[0], y: pl.at[1] } : undefined;
  }

  private guideTo(at: { x: number; y: number } | undefined): void {
    if (at) this.h.world.story.guide = { x: at.x, y: at.y };
  }

  /** The chain is past this quest (handed in). */
  private past(id: string): boolean {
    const k = this.ix(id);
    return k >= 0 && this.h.world.progress.quest.index > k;
  }
}

/** The mark over an NPC: the plains story's when it runs, else the elder's chain (the 72 m flow, as before). */
export function npcMark(w: World, e: Entity): NpcMark {
  const s = plainsStoryOf(w);
  if (s?.active) return s.markOf(e);
  if (e === w.elder) {
    const q = w.currentQuest();
    const st = w.progress.quest.status;
    return q && st === 'available' ? 'offer' : q && st === 'ready' ? 'ready' : null;
  }
  return w.progress.school ? null : 'recruit';
}

/** The quest strip's line for a quest on offer or done: who to talk to (the elder unless the quest names another). */
export function questGiverLine(q: QuestDef, status: 'available' | 'ready'): string {
  const who = q.giver ?? { name: 'ผู้เฒ่าหลิว', where: 'ที่ศิลาเก้ากระบี่' };
  return status === 'available' ? `คุยกับ${who.name}${who.where}` : `สำเร็จ · กลับไปหา${who.name}`;
}
